import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { PageHeader, Panel, Spinner } from "../components/Panels.js";

interface EscalationRow {
  id: string;
  staff_id: string;
  staff_name: string;
  business_unit: string | null;
  reason: string;
  severity: "low" | "medium" | "high" | "urgent";
  description: string | null;
  action_type: string;
  meeting_with: string | null;
  meeting_scheduled_for: string | null;
  status: string;
  resolution_notes: string | null;
  created_at: string;
}

const SEV_CLASS: Record<string, string> = {
  urgent: "bg-sentiment-overwhelmed/20 text-sentiment-overwhelmed border-sentiment-overwhelmed/40",
  high: "bg-sentiment-frustrated/20 text-sentiment-frustrated border-sentiment-frustrated/40",
  medium: "bg-sentiment-concerned/20 text-sentiment-concerned border-sentiment-concerned/40",
  low: "bg-bg-surface text-ink-dim border-bg-line2",
};

export function EscalationsPage() {
  const [rows, setRows] = useState<EscalationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = () => {
    api
      .get<{ escalations: EscalationRow[] }>("/escalations?status=open")
      .then((r) => setRows(r.escalations))
      .catch((e: Error) => setError(e.message));
  };

  useEffect(refresh, []);

  const act = async (id: string, action: "resolve" | "dismiss" | "reschedule", body?: unknown) => {
    setBusy(id + ":" + action);
    try {
      await api.post(`/escalations/${id}/${action}`, body);
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (error) return <div className="p-6 text-sm text-sentiment-overwhelmed">{error}</div>;
  if (!rows) return <div className="p-6"><Spinner /></div>;

  return (
    <>
      <PageHeader
        title="Escalations"
        subtitle="Open and scheduled escalations across all business units."
      />
      <div className="px-6">
        <Panel title={`${rows.length} open / scheduled`}>
          {rows.length === 0 ? (
            <div className="text-sm text-ink-dim italic">Nothing to triage right now.</div>
          ) : (
            <ul className="divide-y divide-bg-line">
              {rows.map((e) => (
                <li key={e.id} className="py-4 flex items-start gap-4">
                  <span
                    className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border ${
                      SEV_CLASS[e.severity] ?? SEV_CLASS.low
                    }`}
                  >
                    {e.severity}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink">
                      {e.staff_name}
                      <span className="text-ink-dimmer mx-2">·</span>
                      <span className="text-ink-dim">{e.business_unit ?? "—"}</span>
                      <span className="text-ink-dimmer mx-2">·</span>
                      <span className="text-ink-dim text-xs">
                        {labelReason(e.reason)} → {labelAction(e.action_type)}
                      </span>
                    </div>
                    <p className="text-xs text-ink-dim mt-1">{e.description ?? "No details."}</p>
                    {e.meeting_with ? (
                      <div className="text-[11px] text-ink-dimmer mt-1">
                        Meeting with {e.meeting_with}
                        {e.meeting_scheduled_for ? ` · ${e.meeting_scheduled_for}` : ""}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={busy === e.id + ":resolve"}
                      onClick={() => act(e.id, "resolve", { resolution_notes: "Handled" })}
                      className="text-xs px-2 py-1 rounded bg-sentiment-positive/20 text-sentiment-positive border border-sentiment-positive/40"
                    >
                      Resolve
                    </button>
                    <button
                      type="button"
                      disabled={busy === e.id + ":dismiss"}
                      onClick={() => act(e.id, "dismiss")}
                      className="text-xs px-2 py-1 rounded border border-bg-line2 text-ink-dim"
                    >
                      Dismiss
                    </button>
                    <button
                      type="button"
                      disabled={busy === e.id + ":reschedule"}
                      onClick={() => {
                        const t = window.prompt(
                          "New meeting time (ISO-8601):",
                          e.meeting_scheduled_for ?? "",
                        );
                        if (t) act(e.id, "reschedule", { meeting_scheduled_for: t });
                      }}
                      className="text-xs px-2 py-1 rounded border border-bg-line2 text-ink-dim"
                    >
                      Reschedule
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}

function labelReason(r: string): string {
  return (
    {
      no_response: "No response",
      blocker: "Blocker",
      behind_on_goals: "Behind on goals",
      negative_sentiment: "Negative sentiment",
      repeated_blocker: "Repeated blocker",
      manual: "Manual flag",
    } as Record<string, string>
  )[r] ?? r;
}
function labelAction(a: string): string {
  return (
    {
      manager_meeting: "Manager meeting",
      ceo_meeting: "CEO meeting",
      ceo_dm: "CEO DM",
      no_action: "No action",
    } as Record<string, string>
  )[a] ?? a;
}
